import { useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import { Button, Modal } from '@insight-health-ai/roseate/components';
import {
  DotsVertical,
  Edit01,
  Trash01,
} from '@insight-health-ai/roseate/icons';
import { Comment, Practitioner } from 'src/models';
import { getPractitionerName } from 'src/utils';
import { PractitionerName } from 'src/components/practitioner';
import { useDeleteComment } from 'src/hooks/comment';
import { useGetCurrentPractitioner } from 'src/hooks/settings';

dayjs.extend(relativeTime);

const MENTION_ID_PATTERN = /@\{practitioner_id:(\d+)\}/g;

const renderBodyWithMentions = (
  body: string,
  practitionerByIdMap: Map<number, Practitioner>
) => {
  const parts: (string | JSX.Element)[] = [];
  let lastIdx = 0;
  const re = new RegExp(MENTION_ID_PATTERN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIdx) parts.push(body.slice(lastIdx, m.index));
    const id = Number(m[1]);
    const p = practitionerByIdMap.get(id);
    const displayName = p ? getPractitionerName(p) || `#${id}` : `#${id}`;
    parts.push(
      <span key={m.index} className="font-semibold text-blue-700">
        @{displayName}
      </span>
    );
    lastIdx = re.lastIndex;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts.length ? parts : body;
};

type CommentItemProps = {
  comment: Comment;
  practitionerByIdMap: Map<number, Practitioner>;
  disabled?: boolean;
  onEdit?: (comment: Comment) => void;
};

export const CommentItem = ({
  comment,
  practitionerByIdMap,
  disabled,
  onEdit,
}: CommentItemProps) => {
  const { data: currentPractitioner, isSuperAdmin } =
    useGetCurrentPractitioner();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAuthor =
    currentPractitioner?.practitioner_id === comment.author_practitioner_id;
  const canDelete = isAuthor || isSuperAdmin;
  const canEdit = isAuthor;

  const authorPractitioner = practitionerByIdMap.get(
    comment.author_practitioner_id
  ) ?? {
    practitioner_id: comment.author_practitioner_id,
    first_name: 'Practitioner',
    last_name: `#${comment.author_practitioner_id}`,
  };

  const { mutateAsync: deleteComment } = useDeleteComment();

  const handleDelete = () => {
    Modal.confirm({
      appearance: 'error',
      title: 'Delete note',
      content: 'Are you sure you want to delete this note?',
      primaryAction: {
        onClick: async () =>
          await deleteComment({
            commentId: comment.id,
            entityType: comment.entity_type,
            entityId: comment.entity_id,
          }),
        label: 'Delete',
      },
    });
  };

  const menuItems = [
    canEdit && {
      icon: <Edit01 />,
      key: 'edit',
      label: 'Edit',
      onClick: () => onEdit?.(comment),
    },
    canDelete && {
      icon: <Trash01 />,
      key: 'delete',
      label: <span>Delete</span>,
      onClick: handleDelete,
      danger: true,
    },
  ].filter(Boolean) as NonNullable<MenuProps['items']>;

  const createdAt = comment.created_at ? dayjs(comment.created_at) : null;
  const editedAt = comment.edited_at ? dayjs(comment.edited_at) : null;

  return (
    <div className="flex items-start gap-2">
      <PractitionerName
        practitioner={authorPractitioner}
        avatar={{ size: 32 }}
        showName={false}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-x-2 gap-y-0 flex-wrap leading-tight">
            <span className="font-medium">
              <PractitionerName
                practitioner={authorPractitioner}
                avatar={false}
              />
            </span>
            {createdAt && (
              <span className="text-fg-muted text-xs flex items-center gap-1">
                {createdAt.fromNow()}
                {editedAt && (
                  <span className="text-fg-muted text-xs">(edited)</span>
                )}
              </span>
            )}
          </div>
          {menuItems.length > 0 && (
            <Dropdown
              disabled={disabled}
              open={!disabled && menuOpen}
              onOpenChange={setMenuOpen}
              trigger={['click']}
              menu={{ items: menuItems }}
              placement="bottomRight"
            >
              <Button
                variant="text"
                appearance="secondary"
                size="sm"
                icon={<DotsVertical />}
              />
            </Dropdown>
          )}
        </div>

        <div className="whitespace-pre-wrap text-sm text-gray-800">
          {renderBodyWithMentions(comment.body, practitionerByIdMap)}
        </div>
      </div>
    </div>
  );
};
